import { DemoEntryAnalysisApiError } from "../services/demoEntryAnalysisApi";

export const DEMO_SYNC_OTHER_CSV_COPY =
  "Unfortunately, we couldn’t locate this instrument in our data sources. Please sync a CSV with other instruments.";

export const DEMO_NONE_LOCATABLE_COPY =
  "Unfortunately, we couldn’t locate the instruments in this file in our data sources. Please sync a CSV with other instruments.";

/** Re-export so Demo wizard can share the Databento ~48h futures gate copy. */
export { DEMO_FUTURES_RECENT_TAIL_COPY } from "./demoFuturesRecentTailGate";
export const DEMO_SESSION_EXPIRED_COPY =
  "This demo session expired or could not be verified. Upload your trades again to continue.";

export const DEMO_TES_DAILY_LIMIT_COPY =
  "You’ve used today’s free demo analysis. Come back tomorrow, or subscribe for full reports.";

export function isDemoPollUnauthorized(err: unknown): boolean {
  if (!(err instanceof DemoEntryAnalysisApiError)) return false;
  if (err.status === 403) return true;
  const slug = String(err.slug || "").toLowerCase();
  return slug.includes("poll_unauthorized") || slug.includes("handoff_invalid");
}

/** Redeem failed because the single-use handoff_code is gone/invalid — not necessarily a dead job. */
export function isDemoHandoffInvalid(err: unknown): boolean {
  if (!(err instanceof DemoEntryAnalysisApiError)) return false;
  const slug = String(err.slug || "").toLowerCase();
  return slug.includes("handoff_invalid");
}

/** IP already has a prep/TES job in flight (429, not a quota cooldown). */
export function isDemoEntryAnalysisInflight(err: unknown): boolean {
  if (!(err instanceof DemoEntryAnalysisApiError)) return false;
  return String(err.slug || "").toLowerCase().includes("entry_analysis.inflight");
}

export function isDemoEntryAnalysisInflightSlug(slug: string | undefined): boolean {
  return String(slug || "").toLowerCase().includes("entry_analysis.inflight");
}

export function isDemoRateLimited(err: unknown): boolean {
  if (!(err instanceof DemoEntryAnalysisApiError)) return false;
  // Inflight / daily TES use 429 but are not generic rate-limit cooldowns.
  if (isDemoEntryAnalysisInflight(err) || isDemoTesDailyUserLimit(err)) return false;
  if (err.status === 429 || err.status === 503) return true;
  return String(err.slug || "").toLowerCase().includes("rate_limit");
}

/** Daily per-user TES budget — claim was rejected; do not retry today. */
export function isDemoTesDailyUserLimit(err: unknown): boolean {
  if (!(err instanceof DemoEntryAnalysisApiError)) return false;
  const slug = String(err.slug || "").toLowerCase();
  return slug.includes("tes_daily_user") || slug.includes("tes_daily");
}

function formatRetryAfter(seconds: number | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const mins = Math.max(1, Math.ceil(seconds / 60));
  return mins === 1 ? "about 1 minute" : `about ${mins} minutes`;
}

export function isSyncOtherCsvCta(cta: string | null | undefined): boolean {
  return String(cta || "").trim().toLowerCase() === "sync_other_csv";
}

export const DEMO_INFLIGHT_COPY =
  "You already have a demo report preparing. Continue to that report, or choose Start over to begin again.";

export function demoErrorUserMessage(err: unknown): string {
  if (err instanceof DemoEntryAnalysisApiError) {
    if (isSyncOtherCsvCta(err.cta)) return DEMO_SYNC_OTHER_CSV_COPY;
    if (isDemoEntryAnalysisInflight(err)) {
      return err.message?.trim() || DEMO_INFLIGHT_COPY;
    }
    if (isDemoTesDailyUserLimit(err)) {
      return err.message?.trim() || DEMO_TES_DAILY_LIMIT_COPY;
    }
    if (isDemoRateLimited(err)) {
      if (err.message?.trim()) return err.message.trim();
      const wait = formatRetryAfter(err.retryAfterSeconds);
      return wait
        ? `Too many demo requests right now. Please try again in ${wait}.`
        : "We’re receiving a lot of demo requests right now. Please try again in a few minutes.";
    }
    return err.message || DEMO_SYNC_OTHER_CSV_COPY;
  }
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  return "Something went wrong. Please try again.";
}

export function demoErrorCta(err: unknown): string | null {
  if (err instanceof DemoEntryAnalysisApiError) return err.cta ?? null;
  return null;
}
